const guides = {
  indexTitle: 'Hospitality ops guides',
  indexIntro:
    'Short, practical guides for floor managers and owners. Use them on paper first if you want, then run the same routines in GastroBuddy.',
  pickLabel: 'Browse guides',
  cta: 'Start my free month',
  ctaHint: 'Turn this into live task lists for your team',
  seeVerticals: 'GastroBuddy by venue type →',
  seeChecklist: 'Free open/close checklist →',
  otherGuides: 'More guides',
  readTime: 'About {minutes} min read',
  'open-close': {
    metaTitle: 'Open/close checklist guide for restaurants & bars',
    metaDescription:
      'How to design opening and closing routines your team will actually follow — then run them in GastroBuddy.',
    title: 'Open and close without the binder',
    intro:
      'Strong open/close lists reduce surprises at service and at lock-up. Keep them short, owned, and visible.',
    minutes: '4',
    sections: {
      s1Title: '1. Split open and close',
      s1Body:
        'One giant list gets skipped. Separate opening, mid-shift, and closing so the right people see the right steps.',
      s2Title: '2. Name the owner',
      s2Body:
        'Every item should imply who does it (opener, closer, bar, kitchen). Ambiguous lists create gaps.',
      s3Title: '3. Keep it floor-length',
      s3Body:
        'If it takes longer to read than to do, cut it. Aim for the critical path, not a novel.',
      s4Title: '4. Make progress visible',
      s4Body:
        'Managers should see what is done without interrupting service. That is where a shared app beats paper.',
      s5Title: '5. Review weekly',
      s5Body:
        'Drop steps that never matter; add the ones that caused last week’s issues. Lists should evolve with the venue.',
    },
    tip: 'Start from our free printable open/close checklist, then migrate the same structure into GastroBuddy.',
  },
  'staff-onboarding': {
    metaTitle: 'Onboard hospitality staff with step-by-step plans',
    metaDescription:
      'A practical approach to onboarding new floor staff with checklists instead of tribal knowledge only.',
    title: 'Onboard staff with plans, not only shadowing',
    intro:
      'Shadow shifts matter — but without a written path, quality depends on who trained whom.',
    minutes: '5',
    sections: {
      s1Title: '1. Day-one essentials only',
      s1Body:
        'Safety, guest flow, tills, and the top five station tasks. Everything else can wait for week two.',
      s2Title: '2. Role-based lists',
      s2Body:
        'Server, bar, kitchen, and closer paths should differ. One mega-list overwhelms new hires.',
      s3Title: '3. Show completion',
      s3Body:
        'Let trainers and managers see what the new hire has practiced — without hovering all shift.',
      s4Title: '4. Repeat until boring',
      s4Body:
        'Consistency beats cleverness. The same open/close list builds muscle memory faster than ad-hoc tips.',
      s5Title: '5. Hand off to the team',
      s5Body:
        'When the newbie can complete the list without a buddy, they are closer to independence.',
    },
    tip: 'GastroBuddy is built for step-by-step plans your team can follow on phone or tablet.',
  },
  'haccp-checks': {
    metaTitle: 'HACCP-style checks on the floor — practical guide',
    metaDescription:
      'How to keep food-safety style checks short, timely, and actually completed during service.',
    title: 'HACCP-style checks that get done',
    intro:
      'Food-safety routines fail when they are buried in binders. Put critical checks where the team already works.',
    minutes: '5',
    sections: {
      s1Title: '1. Critical control first',
      s1Body:
        'Temps, cooling, allergen communication, cleaning of high-risk surfaces — not every nice-to-have.',
      s2Title: '2. Time-box the checks',
      s2Body:
        'Attach checks to open, mid-shift, and close so they happen when the workflow already pauses.',
      s3Title: '3. Record without theatre',
      s3Body:
        'Logging should take seconds. If staff dread the form, they will skip it under pressure.',
      s4Title: '4. Escalate exceptions',
      s4Body: 'A failed temp check needs a clear next step — not just a blank line in a book.',
      s5Title: '5. Keep proof accessible',
      s5Body: 'Managers and auditors need history. Digital lists beat pages that go missing.',
    },
    tip: 'Use GastroBuddy task lists for recurring checks; adapt items to your own food-safety plan and legal advice.',
  },
  'cash-closing': {
    metaTitle: 'Cash closing checklist for hospitality teams',
    metaDescription:
      'A calm close-out: floats, tips, card totals, and handoff — without a midnight scramble.',
    title: 'Cash closing without the scramble',
    intro:
      'Day closings go wrong when cash lives in one notebook and tasks live in another. Unify the routine.',
    minutes: '4',
    sections: {
      s1Title: '1. Standardize the sequence',
      s1Body: 'Count, tips, card reconciliation, variances, secure drop — same order every night.',
      s2Title: '2. Two-person habits where needed',
      s2Body:
        'For high-risk steps, define when a second person should verify. Clarity beats improvisation.',
      s3Title: '3. Capture variances early',
      s3Body:
        'Note differences the same night with a short reason. Morning archaeology is expensive.',
      s4Title: '4. Link close-out to floor close',
      s4Body:
        'Cash should be a chapter of closing, not a separate ritual that starts after everyone left.',
      s5Title: '5. Keep history',
      s5Body: 'Owners need trends across days. Scattered papers hide patterns.',
    },
    tip: 'GastroBuddy includes cash register tracking and day closings in the same account as your floor lists.',
  },
  'mise-en-place': {
    metaTitle: 'Mise en place lists for kitchen and bar',
    metaDescription: 'How to structure MEP and prep lists so production stays ahead of service.',
    title: 'Mise en place that stays ahead of service',
    intro:
      'MEP lists fail when they are tribal. Write them as reusable prep plans tied to recipes and stations.',
    minutes: '4',
    sections: {
      s1Title: '1. Station-based batches',
      s1Body: 'Group prep by station or event, not by random shopping thoughts.',
      s2Title: '2. Tie prep to recipes',
      s2Body:
        'When specs change, prep should update with them — not live in a separate sticky note.',
      s3Title: '3. Par levels in plain language',
      s3Body: '“Two Lexans of diced onion” beats vague “enough for Friday”.',
      s4Title: '4. Close the loop after service',
      s4Body: 'What ran out? What was overproduced? Feed that into tomorrow’s list.',
      s5Title: '5. Share across kitchen and bar',
      s5Body:
        'Bar garnish prep and kitchen MEP often collide at the same fridge. One system helps.',
    },
    tip: 'GastroBuddy supports recipes and mise en place lists with kitchen and bar contexts.',
  },
  'multi-location': {
    metaTitle: 'Multi-location hospitality ops — keep standards aligned',
    metaDescription:
      'How owners with more than one venue keep open/close and quality standards consistent.',
    title: 'Multi-location without losing the standard',
    intro:
      'Two venues means two cultures unless routines are explicit. Shared structure, local detail.',
    minutes: '5',
    sections: {
      s1Title: '1. One core list, local variants',
      s1Body:
        'Keep brand-critical steps identical; allow outlet-specific add-ons for layout or concept.',
      s2Title: '2. Compare completion, not vibes',
      s2Body:
        'Managers need to see which location finishes open/close — not rely on “it felt fine”.',
      s3Title: '3. Separate locations cleanly',
      s3Body: 'Staff and modules should map to the right place so progress is not mixed.',
      s4Title: '4. Owner view from the browser',
      s4Body: 'You will not be on every floor. A web dashboard matters as much as the apps.',
      s5Title: '5. Roll out slowly',
      s5Body:
        'Pilot one location, then invite the next team. Forced big-bang rollouts create resistance.',
    },
    tip: 'GastroBuddy is built for locations, modules, and a web dashboard alongside mobile apps.',
  },
} as const;

export default guides;



// WEBPACK FOOTER //
// ./src/i18n/messages/segments/guides.en.ts